import { IFRAME_KEYSTORECHANGE_EVENT, IFRAME_PARENT_DISCONNECTED, } from '@cosmos-kit/core';
import { useCallback, useEffect, useState } from 'react';
import { useWallet } from './useWallet';
export const useIframe = ({ walletName, walletInfo, accountReplacement, walletClientOverrides, aminoSignerOverrides, directSignerOverrides, } = {}) => {
    const wallet = useWallet(walletName).mainWallet;
    const [iframe, setIframe] = useState(null);
    // Broadcast keystore change event to iframe wallet.
    useEffect(() => {
        const notifyIframe = () => {
            iframe?.contentWindow.postMessage({
                event: IFRAME_KEYSTORECHANGE_EVENT,
            }, '*');
        };
        // Notify inner window of keystore change on any wallet client change
        // (likely either connection or disconnection).
        notifyIframe();
        if (!wallet || typeof window === 'undefined') {
            return;
        }
        // Notify inner window of keystore change on any wallet connect event.
        wallet.walletInfo.connectEventNamesOnWindow?.forEach((eventName) => {
            window.addEventListener(eventName, notifyIframe);
        });
        wallet.walletInfo.connectEventNamesOnClient?.forEach(async (eventName) => {
            wallet.client?.on?.(eventName, notifyIframe);
        });
        return () => {
            wallet.walletInfo.connectEventNamesOnWindow?.forEach((eventName) => {
                window.removeEventListener(eventName, notifyIframe);
            });
            wallet.walletInfo.connectEventNamesOnClient?.forEach(async (eventName) => {
                wallet.client?.off?.(eventName, notifyIframe);
            });
        };
    }, [wallet, iframe]);
    // Whenever wallet changes, broadcast keystore change event to iframe wallet.
    useEffect(() => {
        iframe?.contentWindow.postMessage({
            event: IFRAME_KEYSTORECHANGE_EVENT,
        }, '*');
    }, [wallet, iframe]);
    useEffect(() => {
        if (!iframe) {
            return;
        }
        const listener = async (event) => {
            if (event.source !== iframe.contentWindow) {
                return;
            }
            if ('id' in event.data &&
                'method' in event.data &&
                'params' in event.data) {
                const { id, method, params } = event.data;
                let msg;
                try {
                    if (method.startsWith('signer:')) {
                        if (!event.data.chainId || !event.data.signType) {
                            throw new Error('Missing chainId or signType');
                        }
                        const subMethod = method.substring('signer:'.length);
                        // Try amino signer override method.
                        if (aminoSignerOverrides &&
                            event.data.signType === 'amino' &&
                            subMethod in aminoSignerOverrides) {
                            const handledMsg = processOverrideHandler(await aminoSignerOverrides[subMethod](...params));
                            if (handledMsg) {
                                msg = handledMsg;
                            }
                        }
                        // Try direct signer override method.
                        if (directSignerOverrides &&
                            event.data.signType === 'direct' &&
                            subMethod in directSignerOverrides) {
                            const handledMsg = processOverrideHandler(await directSignerOverrides[subMethod](...params));
                            if (handledMsg) {
                                msg = handledMsg;
                            }
                        }
                        // If neither override handles it, run the original method.
                        if (!msg) {
                            // If wallet not connected and no override, throw.
                            if (!wallet) {
                                throw new Error(IFRAME_PARENT_DISCONNECTED);
                            }
                            const signer = event.data.signType === 'direct'
                                ? await wallet.client.getOfflineSignerDirect(event.data.chainId)
                                : await wallet.client.getOfflineSignerAmino(event.data.chainId);
                            if (!(subMethod in signer) ||
                                typeof signer[subMethod] !== 'function') {
                                throw new Error(`No ${subMethod} ${event.data.signType} signer method for ${event.data.chainId}.`);
                            }
                            const response = await signer[subMethod](...params);
                            // If getting accounts, replace address.
                            if (subMethod === 'getAccounts' && accountReplacement) {
                                const account = await accountReplacement(event.data.chainId);
                                if (account) {
                                    response.forEach((responseAccount) => {
                                        responseAccount.address = account.address;
                                        if (account.pubkey) {
                                            responseAccount.pubkey = account.pubkey;
                                        }
                                    });
                                }
                            }
                            msg = {
                                type: 'success',
                                response,
                            };
                        }
                    }
                    else {
                        // Try override method.
                        if (walletClientOverrides && method in walletClientOverrides) {
                            const handledMsg = processOverrideHandler(await walletClientOverrides[method](...params));
                            if (handledMsg) {
                                msg = handledMsg;
                            }
                        }
                        // If no override handles it, run the original method.
                        if (!msg) {
                            // If wallet not connected and no override, throw.
                            if (!wallet) {
                                throw new Error(IFRAME_PARENT_DISCONNECTED);
                            }
                            const response = method in wallet.client &&
                                typeof wallet.client[method] === 'function'
                                ? await wallet.client[method](...params)
                                : undefined;
                            // If getting accounts, try to replace info.
                            if ((method === 'getAccount' || method === 'getSimpleAccount') &&
                                accountReplacement &&
                                params.length === 1 &&
                                typeof params[0] === 'string') {
                                const account = await accountReplacement(params[0]);
                                if (account) {
                                    response.address = account.address;
                                    if (account.username) {
                                        response.username = account.username;
                                    }
                                    if (account.pubkey && response.pubkey) {
                                        response.pubkey = account.pubkey;
                                    }
                                }
                            }
                            msg = {
                                type: 'success',
                                response,
                            };
                        }
                        // Respond with parent wallet info on successful connect.
                        if (msg.type === 'success' && method === 'connect') {
                            msg.response = {
                                prettyName: walletInfo?.prettyName ||
                                    `${wallet.walletInfo.prettyName} (Outer Wallet)`,
                                logo: walletInfo?.logo || wallet.walletInfo.logo,
                            };
                        }
                    }
                }
                catch (err) {
                    msg = {
                        type: 'error',
                        error: err instanceof Error ? err.message : `${err}`,
                    };
                }
                iframe.contentWindow.postMessage({
                    ...msg,
                    id,
                }, '*');
            }
        };
        window.addEventListener('message', listener);
        return () => {
            window.removeEventListener('message', listener);
        };
    }, [iframe, wallet]);
    const iframeRef = useCallback((iframe) => setIframe(iframe), []);
    return {
        wallet,
        iframeRef,
    };
};
const processOverrideHandler = (handler) => {
    if (!handler || handler.type === 'error') {
        return {
            type: 'error',
            error: (handler && handler.type === 'error' && handler.error) ||
                'Handled by outer wallet.',
        };
    }
    else if (handler.type === 'success') {
        return {
            type: 'success',
            response: handler.value,
        };
    }
};
//# sourceMappingURL=useIframe.js.map